import type { ReadinessProfile } from '../../types/readiness';
import type { EMTProfile } from '../../types/firestore';
import type { PublicProfile } from '../firestore/employerTypes';

export function toReadinessProfile(emt: EMTProfile | null | undefined): ReadinessProfile | null {
  if (!emt) return null;
  return {
    uid: emt.uid,
    firstName: emt.firstName,
    lastName: emt.lastName,
    photoURL: emt.photoURL,
    bio: emt.bio,
    updatedAt: emt.updatedAt,
    preferences: emt.preferences,
    address: emt.address,
    locationText: emt.locationText,
    professional: emt.professional as ReadinessProfile['professional'],
    profileDocuments: emt.profileDocuments,
    pastExperience: emt.pastExperience,
    certificationChecks: (emt as { certificationChecks?: ReadinessProfile['certificationChecks'] }).certificationChecks,
  };
}

export function toReadinessProfileFromPublic(
  publicProfile: PublicProfile | Record<string, unknown> | null | undefined
): ReadinessProfile | null {
  if (!publicProfile) return null;
  const p = publicProfile as Record<string, unknown>;
  const loc = p.location as { city?: string; state?: string } | undefined;
  const skillsRaw = p.skills;
  let skills: Record<string, boolean> | string[] = [];
  if (Array.isArray(skillsRaw)) {
    skills = skillsRaw.reduce((acc: Record<string, boolean>, s) => {
      acc[String(s)] = true;
      return acc;
    }, {});
  }
  // publicProfiles.certifications is stored as [{name: "NREMT-P"}, ...] (see
  // syncPublicProfile), not plain strings — String(entry) on the object form
  // produced "[object Object]", which never matches any job's required certs
  // and made calculateJobMatch report a false "missing required certifications"
  // hard-gate failure that contradicted the correct Job Fit summary shown
  // elsewhere on the same employer application-detail page.
  const certs = Array.isArray(p.certifications)
    ? (p.certifications as unknown[])
        .map((entry, i) => {
          const name = typeof entry === 'string' ? entry : (entry as { name?: string; title?: string } | null)?.name ?? (entry as { title?: string } | null)?.title ?? '';
          return { id: `pub-${i}`, name: String(name || '') };
        })
        .filter((c) => c.name)
    : [];

  return {
    uid: String(p.id || ''),
    photoURL: p.photoURL as string,
    bio: p.bio as string,
    updatedAt: p.updatedAt,
    preferences: p.preferences as ReadinessProfile['preferences'],
    address: loc ? { city: loc.city, state: loc.state } : undefined,
    locationText: p.locationText as string,
    professional: {
      certifications: certs,
      skills,
      experience: (p.experienceSummary as { years?: number }) || {},
    },
    profileDocuments: Array.isArray(p.profileDocuments)
      ? (p.profileDocuments as ReadinessProfile['profileDocuments'])
      : [],
  };
}
